import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from './firebaseConfig';
import {
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  Check,
  Calendar,
  BookOpen,
  Target,
  Compass,
} from 'lucide-react';
import TaskView from './components/TaskView';
import GoalsView from './components/GoalsView';
import NotesView from './components/NotesView';
import CalendarSync from './components/CalendarSync';
import RolesRoutinesView from './components/RolesRoutinesView';
import AuthForm from './components/AuthForm';
import AppHeader from './components/AppHeader';
import PrivacyPolicy from "./PrivacyPolicy";

// ─── Theme definitions ────────────────────────────────────────────────────────
const THEMES = {
  original: {
    '--color-primary':         '#2563eb',
    '--color-primary-hover':   '#1d4ed8',
    '--color-primary-light':   '#eff6ff',
    '--color-secondary':       '#16a34a',
    '--color-secondary-hover': '#15803d',
    '--color-secondary-light': '#f0fdf4',
    '--color-bg-from':         '#f8fafc',
    '--color-bg-via':          '#eff6ff',
    '--color-bg-to':           '#f0fdf4',
  },
  'woods-earth': {
    '--color-primary':         '#3d5a3e',
    '--color-primary-hover':   '#2e4430',
    '--color-primary-light':   '#edf3ed',
    '--color-secondary':       '#7c5c3a',
    '--color-secondary-hover': '#5e4329',
    '--color-secondary-light': '#f7f0e8',
    '--color-bg-from':         '#f5f0e8',
    '--color-bg-via':          '#edf3ed',
    '--color-bg-to':           '#ede8df',
  },
  'coral-reef': {
    '--color-primary':         '#e8613a',
    '--color-primary-hover':   '#c94e2b',
    '--color-primary-light':   '#fff1ec',
    '--color-secondary':       '#0e9090',
    '--color-secondary-hover': '#0a7070',
    '--color-secondary-light': '#e6f7f7',
    '--color-bg-from':         '#fffaf7',
    '--color-bg-via':          '#fff1ec',
    '--color-bg-to':           '#e6f7f7',
  },
  'southwest-desert': {
    '--color-primary':         '#c1603a',
    '--color-primary-hover':   '#a34e2e',
    '--color-primary-light':   '#fdf0ea',
    '--color-secondary':       '#7a9e7e',
    '--color-secondary-hover': '#607e64',
    '--color-secondary-light': '#eef4ef',
    '--color-bg-from':         '#fdf6ee',
    '--color-bg-via':          '#fdf0ea',
    '--color-bg-to':           '#eef4ef',
  },
  pride: {
    '--color-primary':         '#7c3aed',
    '--color-primary-hover':   '#6d28d9',
    '--color-primary-light':   '#f5f3ff',
    '--color-secondary':       '#059669',
    '--color-secondary-hover': '#047857',
    '--color-secondary-light': '#ecfdf5',
    '--color-bg-from':         '#fff7f0',
    '--color-bg-via':          '#f5f3ff',
    '--color-bg-to':           '#f0fdf4',
  },
'transformative-teal': {
    '--color-primary':         '#0d7d7d',
    '--color-primary-hover':   '#0a6363',
    '--color-primary-light':   '#e6f5f5',
    '--color-secondary':       '#c9743d',
    '--color-secondary-hover': '#a85d2e',
    '--color-secondary-light': '#fbf1e8',
    '--color-bg-from':         '#f6faf9',
    '--color-bg-via':          '#eef6f5',
    '--color-bg-to':           '#f4f1eb',
  },
  'warm-linen': {
    '--color-primary':         '#8a6d4f',
    '--color-primary-hover':   '#6f5740',
    '--color-primary-light':   '#f6efe6',
    '--color-secondary':       '#5f7a6b',
    '--color-secondary-hover': '#4b6153',
    '--color-secondary-light': '#eef3ef',
    '--color-bg-from':         '#faf6f0',
    '--color-bg-via':          '#f5efe6',
    '--color-bg-to':           '#f0ece4',
  },
  'violet-cyan': {
    '--color-primary':         '#7c5cff',
    '--color-primary-hover':   '#6544e6',
    '--color-primary-light':   '#f1eeff',
    '--color-secondary':       '#12a4c4',
    '--color-secondary-hover': '#0d8299',
    '--color-secondary-light': '#e6f7fb',
    '--color-bg-from':         '#faf9ff',
    '--color-bg-via':          '#f3f0ff',
    '--color-bg-to':           '#eef8fb',
  },
  'sage-clay': {
    '--color-primary':         '#6b8266',
    '--color-primary-hover':   '#556a51',
    '--color-primary-light':   '#eef3ec',
    '--color-secondary':       '#b5714e',
    '--color-secondary-hover': '#985c3d',
    '--color-secondary-light': '#f8ede6',
    '--color-bg-from':         '#f7f8f4',
    '--color-bg-via':          '#eef3ec',
    '--color-bg-to':           '#f4ede6',
  },
  'slate-amber': {
    '--color-primary':         '#3d5573',
    '--color-primary-hover':   '#2f4258',
    '--color-primary-light':   '#eef2f7',
    '--color-secondary':       '#d98b2b',
    '--color-secondary-hover': '#b87322',
    '--color-secondary-light': '#fdf3e3',
    '--color-bg-from':         '#f7f9fb',
    '--color-bg-via':          '#eef2f7',
    '--color-bg-to':           '#f7f4ee',
  },
};

function applyTheme(themeId) {
  const vars = THEMES[themeId] || THEMES.original;
  const root = document.documentElement;
  Object.entries(vars).forEach(([prop, val]) => {
    root.style.setProperty(prop, val);
  });
}

// ─── Google Calendar constants ────────────────────────────────────────────────
// Replace these with your actual values from Google Cloud Console
const GOOGLE_CLIENT_ID  = '1063887086098-iv45f2pdf2t6agmc3uv411pbqp3pcsjc.apps.googleusercontent.com';
const GOOGLE_API_KEY    = 'AIzaSyDsOGCTxIsTTM4QXgHwUbOk8N8GV6qGOl4';
const GOOGLE_SCOPES     = 'https://www.googleapis.com/auth/calendar.events';
const GOOGLE_DISCOVERY  = 'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest';
// ─────────────────────────────────────────────────────────────────────────────

// ─── Auto-forward incomplete tasks ───────────────────────────────────────────
async function forwardIncompleteTasks(userId) {
  try {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const pastQ = query(
      collection(db, 'tasks'),
      where('userId', '==', userId),
      where('status', 'in', ['pending', 'in-process', 'delegated'])
    );
    const pastSnapshot = await getDocs(pastQ);

    const toForward = pastSnapshot.docs.filter(d => {
      const data = d.data();
      return data.date < todayStr && !data.forwarded;
    });

    if (toForward.length === 0) return;

    const todayQ = query(
      collection(db, 'tasks'),
      where('userId', '==', userId),
      where('date', '==', todayStr)
    );
    const todaySnapshot = await getDocs(todayQ);
    const alreadyForwardedIds = new Set(
      todaySnapshot.docs.map(d => d.data().forwardedFromId).filter(Boolean)
    );

    let forwardedCount = 0;
    for (const taskDoc of toForward) {
      if (alreadyForwardedIds.has(taskDoc.id)) continue;
      const task = taskDoc.data();
      await addDoc(collection(db, 'tasks'), {
        userId:          task.userId,
        title:           task.title,
        category:        task.category,
        status:          'pending',
        date:            todayStr,
        notes:           task.notes || '',
        sortOrder:       task.sortOrder ?? 9999,
        forwarded:       false,
        forwardedFromId: taskDoc.id,
        createdAt:       serverTimestamp(),
      });
      await deleteDoc(doc(db, 'tasks', taskDoc.id));
      forwardedCount++;
    }

    if (forwardedCount > 0) {
      console.log(`[OPP] Auto-forwarded ${forwardedCount} incomplete task(s) to ${todayStr}`);
    }
  } catch (error) {
    console.error('[OPP] Auto-forward error:', error);
  }
}
// ─────────────────────────────────────────────────────────────────────────────

function PrivacyRoute() {
  if (window.location.pathname === '/privacy') {
    return <PrivacyPolicy />;
  }
  return <AppMain />;
}

export default PrivacyRoute;

function AppMain() {
  const [user, setUser]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [currentView, setCurrentView] = useState('tasks');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() => {
    return localStorage.getItem('opp-theme') || 'original';
  });

  // ─── Online/offline ───────────────────────────────────────────────────────
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // ─── Print context ────────────────────────────────────────────────────────
  const [printCurrentDate,   setPrintCurrentDate]   = useState(new Date());
  const [printSelectedYear,  setPrintSelectedYear]   = useState(new Date().getFullYear());
  const [printSelectedMonth, setPrintSelectedMonth] = useState(new Date().getMonth() + 1);

  // ─── Shared Google Calendar auth ──────────────────────────────────────────
  const [gapiReady,      setGapiReady]      = useState(false);
  const [gisReady,       setGisReady]       = useState(false);
  const [googleSignedIn, setGoogleSignedIn] = useState(false);
  const tokenClientRef                      = useRef(null);
  const refreshTimerRef                     = useRef(null);
  const tokenExpiryRef                      = useRef(0);

  // True only if gapi currently holds a token that hasn't expired yet
  const hasLiveToken = () => {
    const token = window.gapi?.client?.getToken();
    return !!token && Date.now() < tokenExpiryRef.current;
  };

  // Silently refresh the token ~5 minutes before it expires,
  // so long sessions never lose the calendar connection.
  const scheduleTokenRefresh = (expiresInSeconds) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const refreshInMs = Math.max((expiresInSeconds - 300) * 1000, 60_000);
    refreshTimerRef.current = setTimeout(() => {
      tokenClientRef.current?.requestAccessToken({ prompt: '' });
    }, refreshInMs);
  };

  useEffect(() => {
    // Load gapi
    const gapiScript    = document.createElement('script');
    gapiScript.src      = 'https://apis.google.com/js/api.js';
    gapiScript.onload   = () => {
      window.gapi.load('client', async () => {
        try {
          await window.gapi.client.init({
            apiKey:        GOOGLE_API_KEY,
            discoveryDocs: [GOOGLE_DISCOVERY],
          });
          setGapiReady(true);
        } catch (e) {
          console.error('[OPP Calendar] gapi init error:', e);
        }
      });
    };
    document.head.appendChild(gapiScript);

    // Load GIS
    const gisScript   = document.createElement('script');
    gisScript.src     = 'https://accounts.google.com/gsi/client';
    gisScript.onload  = () => {
      tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope:     GOOGLE_SCOPES,
        callback:  (tokenResponse) => {
          if (tokenResponse.error) {
            console.warn('[OPP Calendar] token error:', tokenResponse.error);
            setGoogleSignedIn(false);
            return;
          }
          // Remember that this browser has connected before,
          // so we can reconnect silently on future visits.
          localStorage.setItem('opp-gcal-connected', 'true');
          if (tokenResponse.expires_in) {
            tokenExpiryRef.current = Date.now() + Number(tokenResponse.expires_in) * 1000;
            scheduleTokenRefresh(Number(tokenResponse.expires_in));
          }
          setGoogleSignedIn(true);
        },
        error_callback: (err) => {
          // Silent attempt failed (no Google session, popup blocked, etc.)
          // Fall back gracefully — the Connect button will simply show.
          console.warn('[OPP Calendar] auth error:', err?.type || err);
          setGoogleSignedIn(false);
        },
      });
      setGisReady(true);
    };
    document.head.appendChild(gisScript);

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  // Silently (re)connect ONLY when the Calendar view is opened,
  // and ONLY if we don't already hold a live token. This keeps the
  // Google account/consent box from appearing on every app open.
  useEffect(() => {
    if (!gapiReady || !gisReady) return;
    if (currentView !== 'calendar') return;
    if (localStorage.getItem('opp-gcal-connected') !== 'true') return;
    if (hasLiveToken()) {
      setGoogleSignedIn(true);
      return;
    }
    tokenClientRef.current?.requestAccessToken({ prompt: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gapiReady, gisReady, currentView]);

  const handleGoogleSignIn = () => {
    if (!gapiReady || !tokenClientRef.current) return;
    // No prompt option: Google shows the consent screen only if it
    // actually needs to, and connects silently otherwise.
    tokenClientRef.current.requestAccessToken();
  };

  const handleGoogleSignOut = () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    localStorage.removeItem('opp-gcal-connected');
    const token = window.gapi?.client?.getToken();
    if (token) {
      window.google?.accounts.oauth2.revoke(token.access_token);
      window.gapi.client.setToken(null);
    }
    setGoogleSignedIn(false);
  };


  // Returns a promise that resolves once we hold a live calendar token,
  // requesting one silently/interactively if needed. Lets actions like
  // "schedule task to calendar" trigger the connect only at click time.
  const ensureCalendarToken = () =>
    new Promise((resolve, reject) => {
      if (hasLiveToken()) {
        resolve();
        return;
      }
      if (!gapiReady || !tokenClientRef.current) {
        reject(new Error('Google Calendar is still loading. Please try again in a moment.'));
        return;
      }
      // Temporarily wrap the token client's callback so this specific
      // request can resolve/reject, then restore normal behavior.
      const client = tokenClientRef.current;
      const originalCallback = client.callback;
      client.callback = (tokenResponse) => {
        client.callback = originalCallback; // restore
        if (tokenResponse.error) {
          setGoogleSignedIn(false);
          reject(new Error(tokenResponse.error));
          return;
        }
        localStorage.setItem('opp-gcal-connected', 'true');
        if (tokenResponse.expires_in) {
          tokenExpiryRef.current = Date.now() + Number(tokenResponse.expires_in) * 1000;
          scheduleTokenRefresh(Number(tokenResponse.expires_in));
        }
        setGoogleSignedIn(true);
        resolve();
      };
      // Empty prompt: silent if possible, box only if Google requires it.
      client.requestAccessToken({ prompt: '' });
    });

  // Create an event on the user's primary Google Calendar.
  // Timed event:  { title, description?, start: Date, end: Date }
  // All-day event: { title, description?, allDay: true, date: 'yyyy-MM-dd' }
  const createCalendarEvent = async ({ title, description = '', start, end, allDay = false, date }) => {
    await ensureCalendarToken(); // connect on demand if needed

    let event;
    if (allDay) {
      const [y, m, d] = date.split('-').map(Number);
      const next = new Date(y, m - 1, d + 1);
      const nextStr = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
      event = {
        summary:     title,
        description,
        start: { date },
        end:   { date: nextStr },
      };
    } else {
      event = {
        summary:     title,
        description,
        start: { dateTime: start.toISOString() },
        end:   { dateTime: end.toISOString() },
      };
    }

    const response = await window.gapi.client.calendar.events.insert({
      calendarId: 'primary',
      resource:   event,
    });
    return response.result;
  };
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    applyTheme(currentTheme);
    localStorage.setItem('opp-theme', currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) await forwardIncompleteTasks(currentUser.uid);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(to bottom right, var(--color-bg-from), var(--color-bg-via), var(--color-bg-to))' }}
      >
        <div className="text-center">
          <div
            className="inline-block animate-spin rounded-full h-12 w-12 border-b-2"
            style={{ borderColor: 'var(--color-primary)' }}
          ></div>
          <p className="mt-4 text-slate-600">Loading your planner...</p>
        </div>
      </div>
    );
  }

  if (!user) return <AuthForm />;

  const handleLogout = async () => {
    try { await signOut(auth); } catch (error) { console.error('Logout error:', error); }
  };

  const viewConfig = {
    tasks:    { icon: Check,    label: 'My Day',           component: TaskView },
    goals:    { icon: Target,   label: 'Goals',            component: GoalsView },
    notes:    { icon: BookOpen, label: 'Journal',          component: NotesView },
    calendar: { icon: Calendar, label: 'Calendar',         component: CalendarSync },
    roles:    { icon: Compass,  label: 'Roles & Routines', component: RolesRoutinesView },
  };

  const CurrentViewComponent = viewConfig[currentView].component;

  const viewPrintProps = {
    tasks: { onDateChange: (date) => setPrintCurrentDate(date) },
    notes: { onDateChange: (date) => setPrintCurrentDate(date) },
    goals: {
      onYearChange:  (y) => setPrintSelectedYear(y),
      onMonthChange: (m) => setPrintSelectedMonth(m),
    },
  };

  // Shared Google Calendar props — passed to every view;
  // only TaskView and CalendarSync actually use them
  const googleCalendarProps = {
    gapiReady,
    googleSignedIn,
    onGoogleSignIn:  handleGoogleSignIn,
    onGoogleSignOut: handleGoogleSignOut,
    onCreateEvent:   createCalendarEvent,
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: 'linear-gradient(to bottom right, var(--color-bg-from), var(--color-bg-via), var(--color-bg-to))' }}
    >
      {!isOnline && (
        <div style={{
          backgroundColor: '#f59e0b', color: '#1c1917',
          textAlign: 'center', padding: '8px',
          fontSize: '14px', fontWeight: '600', letterSpacing: '0.025em',
        }}>
          ⚡ You're offline — changes will sync when you reconnect
        </div>
      )}
      <AppHeader
        user={user}
        currentView={currentView}
        setCurrentView={setCurrentView}
        viewConfig={viewConfig}
        onLogout={handleLogout}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        currentTheme={currentTheme}
        onSelectTheme={setCurrentTheme}
        currentDate={printCurrentDate}
        selectedYear={printSelectedYear}
        selectedMonth={printSelectedMonth}
      />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <CurrentViewComponent
          user={user}
          {...(viewPrintProps[currentView] || {})}
          {...googleCalendarProps}
        />
      </div>
      
      <footer className="mt-auto py-4 text-center text-sm text-slate-400 border-t border-slate-200 bg-white/50">
        © Robert Pendergrast 2026. All rights reserved.
      </footer>
    </div>
  );
}
